import * as THREE from 'three'
import { TransformControls } from 'three/addons/controls/TransformControls.js'
import { params, saveParams } from '../config/persistence.js'

const isDebug = new URLSearchParams(window.location.search).has('debug')

const makeBarMesh = () => {
    const group = new THREE.Group()

    const geometry = new THREE.BoxGeometry(1, 1, 1)
    const fillMaterial = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.04,
        depthWrite: false,
        side: THREE.DoubleSide,
    })
    const fill = new THREE.Mesh(geometry, fillMaterial)
    group.add(fill)

    const edges = new THREE.EdgesGeometry(geometry)
    const lineMaterial = new THREE.LineBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: isDebug ? 0.5 : 0.18,
    })
    const wire = new THREE.LineSegments(edges, lineMaterial)
    group.add(wire)

    group.userData.fill = fill
    group.userData.wire = wire
    return group
}

const syncMeshFromParams = (mesh, posKey, sizeKey) => {
    const p = params[posKey]
    const s = params[sizeKey]
    mesh.position.set(p.x, p.y, p.z)
    mesh.scale.set(s.x * 2, s.y * 2, s.z * 2)
}

export const setupSuctionBars = ({ scene, camera, renderer, orbitControls, compute: initialCompute }) => {
    let compute = initialCompute

    // Animated values that lerp toward target when toggling on/off (strength only — radius is instant)
    let animatedStrength = params.suctionBarsEnabled ? params.suctionBarStrength : 0
    let animatedFill = params.suctionBarsEnabled ? 1 : 0
    const bar0 = makeBarMesh()
    const bar1 = makeBarMesh()
    bar0.name = 'suctionBar0'
    bar1.name = 'suctionBar1'
    syncMeshFromParams(bar0, 'suctionBar0Pos', 'suctionBar0Size')
    syncMeshFromParams(bar1, 'suctionBar1Pos', 'suctionBar1Size')
    scene.add(bar0, bar1)

    // Hide visuals outside debug mode — physics still active
    if (!isDebug) {
        bar0.visible = false
        bar1.visible = false
    }

    const transformControls = []

    if (isDebug) {
        // mirror = controparte simmetrica: muovendo/scalando una barra, l'altra
        // resta speculare rispetto all'asse verticale (x -> -x), cosi' si spostano
        // in modo parallelo mantenendo la composizione bilanciata
        const attachGizmo = (mesh, posKey, sizeKey, mirror) => {
            const tc = new TransformControls(camera, renderer.domElement)
            tc.setSize(0.65)
            tc.attach(mesh)
            scene.add(tc.getHelper ? tc.getHelper() : tc)
            tc.addEventListener('dragging-changed', (e) => {
                orbitControls.enabled = !e.value
            })
            tc.addEventListener('objectChange', () => {
                if (tc.getMode() === 'translate') {
                    params[posKey] = { x: mesh.position.x, y: mesh.position.y, z: mesh.position.z }
                    params[mirror.posKey] = { x: -mesh.position.x, y: mesh.position.y, z: mesh.position.z }
                    mirror.mesh.position.set(-mesh.position.x, mesh.position.y, mesh.position.z)
                } else if (tc.getMode() === 'scale') {
                    const size = {
                        x: Math.max(0.05, mesh.scale.x / 2),
                        y: Math.max(0.05, mesh.scale.y / 2),
                        z: Math.max(0.05, mesh.scale.z / 2),
                    }
                    params[sizeKey] = { ...size }
                    params[mirror.sizeKey] = { ...size }
                    mirror.mesh.scale.copy(mesh.scale)
                }
                saveParams()
                updateUniforms()
            })
            transformControls.push(tc)
        }
        attachGizmo(bar0, 'suctionBar0Pos', 'suctionBar0Size', { mesh: bar1, posKey: 'suctionBar1Pos', sizeKey: 'suctionBar1Size' })
        attachGizmo(bar1, 'suctionBar1Pos', 'suctionBar1Size', { mesh: bar0, posKey: 'suctionBar0Pos', sizeKey: 'suctionBar0Size' })

        window.addEventListener('keydown', (e) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
            if (e.key === 'g' || e.key === 'G') transformControls.forEach(t => t.setMode('translate'))
            if (e.key === 's' || e.key === 'S') transformControls.forEach(t => t.setMode('scale'))
            if (e.key === 'h' || e.key === 'H') transformControls.forEach(t => t.visible = !t.visible)
        })
    }

    const writeUniforms = () => {
        if (!compute || !compute.velocityVariable || !compute.particlesVariable) return
        const vU = compute.velocityVariable.material.uniforms
        const pU = compute.particlesVariable.material.uniforms
        // Enabled flag stays at 1 — the lerped strength gates the actual effect
        vU.uSuctionBarsEnabled.value = 1.0
        pU.uSuctionBarsEnabled.value = 1.0
        vU.uSuctionBarStrength.value = animatedStrength
        pU.uSuctionBarStrength.value = animatedStrength
        vU.uSuctionBarRadius.value = params.suctionBarRadius
        pU.uSuctionBarRadius.value = params.suctionBarRadius
        vU.uSuctionBar0Pos.value.set(params.suctionBar0Pos.x, params.suctionBar0Pos.y, params.suctionBar0Pos.z)
        vU.uSuctionBar0Size.value.set(params.suctionBar0Size.x, params.suctionBar0Size.y, params.suctionBar0Size.z)
        vU.uSuctionBar1Pos.value.set(params.suctionBar1Pos.x, params.suctionBar1Pos.y, params.suctionBar1Pos.z)
        vU.uSuctionBar1Size.value.set(params.suctionBar1Size.x, params.suctionBar1Size.y, params.suctionBar1Size.z)
        pU.uSuctionBar0Pos.value.copy(vU.uSuctionBar0Pos.value)
        pU.uSuctionBar0Size.value.copy(vU.uSuctionBar0Size.value)
        pU.uSuctionBar1Pos.value.copy(vU.uSuctionBar1Pos.value)
        pU.uSuctionBar1Size.value.copy(vU.uSuctionBar1Size.value)
    }

    const targetStrength = () => params.suctionBarsEnabled ? params.suctionBarStrength : 0
    const targetFill = () => params.suctionBarsEnabled ? 1 : 0

    const baseWireOpacity = 0.5
    const baseFillOpacity = 0.04

    const applyMeshOpacity = () => {
        if (!isDebug) return
        bar0.userData.wire.material.opacity = baseWireOpacity * animatedFill
        bar1.userData.wire.material.opacity = baseWireOpacity * animatedFill
        bar0.userData.fill.material.opacity = baseFillOpacity * animatedFill
        bar1.userData.fill.material.opacity = baseFillOpacity * animatedFill
    }

    const update = (deltaTime) => {
        const speed = params.suctionFadeSpeed ?? 2.5
        const alpha = 1 - Math.exp(-speed * deltaTime)
        animatedStrength += (targetStrength() - animatedStrength) * alpha
        animatedFill += (targetFill() - animatedFill) * alpha
        writeUniforms()
        applyMeshOpacity()
    }

    // Instant sync of static fields (positions / sizes) without touching animated values
    const updateUniforms = () => {
        writeUniforms()
        applyMeshOpacity()
    }

    const syncMeshesFromParams = () => {
        syncMeshFromParams(bar0, 'suctionBar0Pos', 'suctionBar0Size')
        syncMeshFromParams(bar1, 'suctionBar1Pos', 'suctionBar1Size')
    }

    const setCompute = (newCompute) => {
        compute = newCompute
        writeUniforms()
    }

    return {
        bar0,
        bar1,
        transformControls,
        update,
        updateUniforms,
        syncMeshesFromParams,
        setCompute,
    }
}
